import React, { useState, useEffect } from 'react';
import {
    View,
    Text,
    StyleSheet,
    Modal,
    TouchableOpacity,
    TouchableWithoutFeedback,
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { theme } from '../constants/theme';
import { DatePickerInput } from './DatePickerInput';

interface DateRangeFilterModalProps {
    visible: boolean;
    onClose: () => void;
    onApply: (fromDate: string, toDate: string) => void;
    initialFromDate?: string;
    initialToDate?: string;
}



export const DateRangeFilterModal: React.FC<DateRangeFilterModalProps> = ({
    visible,
    onClose,
    onApply,
    initialFromDate,
    initialToDate,
}) => {
    const getDateString = (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const getLastWeekRange = () => {
        const today = new Date();
        const lastWeek = new Date(today);
        lastWeek.setDate(today.getDate() - 7);
        return {
            from: getDateString(lastWeek),
            to: getDateString(today)
        };
    };

    const [fromDate, setFromDate] = useState(() => {
        return initialFromDate || getLastWeekRange().from;
    });
    const [toDate, setToDate] = useState(() => {
        return initialToDate || getLastWeekRange().to;
    });

    useEffect(() => {
        if (visible) {
            // If no initial dates provided, reset to last week
            if (!initialFromDate && !initialToDate) {
                const range = getLastWeekRange();
                setFromDate(range.from);
                setToDate(range.to);
            } else {
                setFromDate(initialFromDate || '');
                setToDate(initialToDate || '');
            }
        }
    }, [visible, initialFromDate, initialToDate]);

    const handleApply = () => {
        if (fromDate && toDate) {
            onApply(fromDate, toDate);
        }
    };

    return (
        <Modal
            visible={visible}
            transparent
            animationType="fade"
            onRequestClose={onClose}
        >
            <TouchableWithoutFeedback onPress={onClose}>
                <View style={styles.overlay}>
                    <TouchableWithoutFeedback>
                        <View style={styles.modalContainer}>
                            {/* Header */}
                            <View style={styles.header}>
                                <Text style={styles.title}>Filter by Date Range</Text>
                                <TouchableOpacity onPress={onClose} style={styles.closeButton}>
                                    <Icon name="close" size={24} color={theme.colors.text} />
                                </TouchableOpacity>
                            </View>

                            {/* Custom Date Range */}
                            <View style={styles.dateInputsRow}>
                                <View style={styles.dateInputWrapper}>
                                    <DatePickerInput
                                        label="From Date"
                                        value={fromDate}
                                        onChangeDate={setFromDate}
                                        placeholder="Select start date"
                                    />
                                </View>
                                <View style={styles.dateInputWrapper}>
                                    <DatePickerInput
                                        label="To Date"
                                        value={toDate}
                                        onChangeDate={setToDate}
                                        placeholder="Select end date"
                                    />
                                </View>
                            </View>

                            {/* Actions */}
                            <View style={styles.actions}>
                                <TouchableOpacity
                                    style={styles.cancelButton}
                                    onPress={onClose}
                                    activeOpacity={0.7}
                                >
                                    <Text style={styles.cancelButtonText}>Cancel</Text>
                                </TouchableOpacity>
                                <TouchableOpacity
                                    style={[
                                        styles.applyButton,
                                        (!fromDate || !toDate) && styles.applyButtonDisabled,
                                    ]}
                                    onPress={handleApply}
                                    disabled={!fromDate || !toDate}
                                    activeOpacity={0.7}
                                >
                                    <Icon name="check" size={18} color="#FFFFFF" />
                                    <Text style={styles.applyButtonText}>Apply Filter</Text>
                                </TouchableOpacity>
                            </View>
                        </View>
                    </TouchableWithoutFeedback>
                </View>
            </TouchableWithoutFeedback>
        </Modal>
    );
};

const styles = StyleSheet.create({
    overlay: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        justifyContent: 'center',
        alignItems: 'center',
        padding: theme.spacing.lg,
    },
    modalContainer: {
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.xl,
        padding: theme.spacing.lg,
        width: '100%',
        maxWidth: 400,
        ...theme.shadows.lg,
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing.lg,
    },
    title: {
        ...theme.typography.h5,
        color: theme.colors.text,
        fontWeight: '700',
    },
    closeButton: {
        padding: theme.spacing.xs,
    },
    dateInputsRow: {
        flexDirection: 'row',
        gap: theme.spacing.md,
        marginBottom: theme.spacing.lg,
    },
    dateInputWrapper: {
        flex: 1,
    },
    actions: {
        flexDirection: 'row',
        gap: theme.spacing.md,
        marginTop: theme.spacing.md,
    },
    cancelButton: {
        flex: 1,
        paddingVertical: theme.spacing.md,
        borderRadius: theme.borderRadius.lg,
        backgroundColor: theme.colors.background,
        alignItems: 'center',
        justifyContent: 'center',
    },
    cancelButtonText: {
        ...theme.typography.body1,
        color: theme.colors.textSecondary,
        fontWeight: '600',
    },
    applyButton: {
        flex: 1,
        flexDirection: 'row',
        paddingVertical: theme.spacing.md,
        borderRadius: theme.borderRadius.lg,
        backgroundColor: theme.colors.primary,
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing.xs,
    },
    applyButtonDisabled: {
        backgroundColor: theme.colors.textLight,
    },
    applyButtonText: {
        ...theme.typography.body1,
        color: '#FFFFFF',
        fontWeight: '600',
    },
});
